import React, { useEffect, useCallback, useState, useMemo, memo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSidebar } from '../../../contexts/SidebarContext';
import { getAgentBySlug, getAgentByChatId } from '../../../api/agents/agents';
import MainLayout from '../../layout/MainLayout';
import ContentArea from '../../layout/ContentArea';
import Sidebar from '../../layout/Sidebar';
import Button from '../../common/button/Button';
import ChatArea from '../../chats/ChatArea';
import { useAuth } from '../../../contexts/AuthContext';

import styles from './styles/AgentChatContainer.module.css';

const AgentChatContainer = () => {
    const { agentSlug, chatId } = useParams();
    const navigate = useNavigate();
    const { setPage: setSidebarPage, setIsSidebarCollapsed } = useSidebar();
    const { isAuthenticated } = useAuth();
    const [agent, setAgent] = useState(null);
    const [error, setError] = useState(null);

    // Fetch agent details
    useEffect(() => {
        const fetchAgent = async () => {
            try {
                if (agentSlug) {
                    const data = await getAgentBySlug(agentSlug);
                    setAgent(data);
                } else if (chatId) {
                    const data = await getAgentByChatId(chatId);
                    setAgent(data);
                }
            } catch (err) {
                console.error('Error fetching agent:', err);
                setError(err.message || 'Failed to load agent');
            }
        };

        fetchAgent();
    }, [agentSlug, chatId]);

    useEffect(() => {
        document.title = agent ? `JetChats - Chat with ${agent.name}` : 'JetChats - Agent Chat';
        setSidebarPage('agents');
    }, [setSidebarPage, agent]);

    const handleNewChat = useCallback(() => {
        if (agent?.slug) {
            navigate(`/agent/${agent.slug}`);
        } else {
            navigate('/agents');
        }
    }, [navigate, agent]);

    const handleBackToAgent = useCallback(() => {
        if (agent?.slug) {
            navigate(`/agents/${agent.slug}`);
        } else {
            navigate('/agents');
        }
    }, [navigate, agent]);

    const isGuest = useMemo(() => isAuthenticated === false, [isAuthenticated]);

    useEffect(() => {
        // For public chats (guest), ensure sidebar is collapsed
        if (isGuest) {
            setIsSidebarCollapsed(true);
        }
    }, [isGuest, setIsSidebarCollapsed]);

    if (error) {
        return (
            <MainLayout>
                {isAuthenticated && <Sidebar />}
                <ContentArea>
                    <div className={styles.errorContainer}>
                        <h2>Error</h2>
                        <p>{error || 'Agent not found'}</p>
                        <Button onClick={() => navigate('/agents')} variant="secondary">
                            {isGuest ? 'Authenticate to continue' : 'Back to Agents'}
                        </Button>
                    </div>
                </ContentArea>
            </MainLayout>
        );
    }

    return (
        <MainLayout>
            {isAuthenticated && <Sidebar handleNewChat={handleNewChat} newChatType="agent" />}
            <ContentArea>
                <ChatArea
                    agent={agent}
                    isAgentChat={true}
                    onBackToAgent={handleBackToAgent}
                    isGuest={isGuest}
                />
            </ContentArea>
        </MainLayout>
    );
};

export default memo(AgentChatContainer);
